import _ from 'underscore';

import { userIdentityConflictSortOptions } from '~src/authentication/models/sort/userIdentityConflictSortOptions';
import { ConnectedApplicationContentType } from '~src/connectedApplications/models/ConnectedApplicationContentType';
import { connectedApplicationSortOptions } from '~src/connectedApplications/models/ConnectedApplicationSortOptions';
import { resourceGovernanceSettingSortOptions } from '~src/schedule/types/ResourceGovernanceSettingSortOptions';
import { NavLocationSelectors } from '~src/spacesPlatform/selectors/NavigationStateSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import {
  DatasourceSubspaceName,
  FlowSubspaceName,
  GroupSubspaceName,
  groupSubspaceNameToExplorableItemType,
  isAllSiteUsersSpace,
  isBackgroundJobsSpace,
  isCatalogSpace,
  isCollectionsSpace,
  isConnectedApplicationSpace,
  isDataRoleSpace,
  isDatasourceSpace,
  isFavoritesSpace,
  isFlowSpace,
  isGroupSpace,
  isPersonalSpace,
  isProjectSpace,
  isPublishedConnectionSpace,
  isPublishedConnectionTableSpace,
  isRecentsSpace,
  isRecommendationsSpace,
  isResourceGovernanceSpace,
  isScheduleSpace,
  isSearchSpace,
  isServerTasksSpace,
  isSharedWithMeSpace,
  isSingleCollectionSpace,
  isSiteSpace,
  isSiteTasksSpace,
  isSiteUserSpace,
  isUnifiedSearchSpace,
  isVizSpace,
  isWorkbookSpace,
  PublishedConnectionSubspaceName,
  PublishedConnectionTableSubspaceName,
  ScheduleSubspaceName,
  scheduleSubspaceNameToExplorableItemType,
  SearchSubspaceName,
  ServerTasksSubspaceName,
  serverTasksSubspaceNameToExplorableItemType,
  SiteTasksSubspaceName,
  siteTasksSubspaceNameToExplorableItemType,
  SpaceInfo,
  subspaceNameToCatalogContentType,
  WorkbookSubspaceName,
} from '~src/spacesPlatform/state/SpaceInfo';

import { getAppStore } from '../../../app/AppStore';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../../../app/state/SiteSpaceContentTypeState';
import { databaseDropdownSortOptions, tableDropdownSortOptions } from '../../../catalog/models/sort/CatalogSortOptions';
import { databaseWithCRASSSortOptions } from '../../../catalog/models/sort/DatabaseWithCrassDropdownSortOptions';
import { tableWithCRASSSortOptions } from '../../../catalog/models/sort/TableWithCrassDropdownSortOptions';
import {
  collectionItemDateSortOrder,
  collectionItemPositionSortOrder,
  collectionItemsMixedContentOptionsBase,
  collectionItemsSpecificSortOptions,
  collectionItemsSpecificSortOptionsWithoutCustomSort,
} from '../../../Collections/models/CollectionItemsSortOptions';
import {
  collectionsSortOptions,
  collectionsSortOptionsWithCollectionsGA,
  defaultCollectionSort,
  getCollectionsSortOptions,
} from '../../../Collections/models/CollectionSortOptions';
import { dataAccelerationSortOptions } from '../../../dataAcceleration/models/sort/DataAccelerationSortOptions';
import { dataRoleSortOptions } from '../../../dataRole/models/sort/DataRoleSortOptions';
import { datasourceSortOptions, datasourceSortOptionsForSearch } from '../../../datasource/models/sort/DatasourceSortOptions';
import { DatasourceSubspaceConnectionsSortOptions } from '../../../datasource/models/sort/DatasourceSubplaceSortOptions';
import { dataAlertSortOptions } from '../../../dda/models/sort/DataAlertSortOptions';
import { extractRefreshSortOptions } from '../../../extractRefresh/models/sort/ExtractRefreshSortOptions';
import { remoteRefreshSortOptions } from '../../../extractRefresh/models/sort/RemoteRefreshSortOptions';
import { favoritesMixedContentOptions, favoritesSpaceDefaultSortOrder } from '../../../favorites/models/FavoritesSortOptions';
import {
  FlowConnectionsSortOptions,
  flowOutputStepRunSortOptions,
  flowSortOptions,
  runFlowTasksSortOptions,
} from '../../../flow/models/sort/FlowSortOptions';
import { lensSortOptions } from '../../../lens/models/sort/LensSortOptions';
import { MetricContentType } from '../../../metrics/models/MetricContentType';
import { metricsSortOptions } from '../../../metrics/models/sort/MetricsSortOptions';
import { predictiveModelSortOptions } from '../../../predictiveModel/models/sort/PredictiveModelSortOptions';
import { publishedConnectionColumnSortOptions, publishedConnectionSortOptions, publishedConnectionTableSortOptions } from '../../../publishedConnection/models/sort/PublishedConnectionSortOptions';
import { recentsSortOptions, recentsSpaceDefaultSortOrder, recentViewsOptions } from '../../../recents/models/RecentsSortOptions';
import { sharedWithMeDefaultSortOrder, sharedWithMeSortOptions } from '../../../sharing/models/sort/SharedWithMeSortOptions';
import { siteSortOptions } from '../../../siteManagement/models/sort/SiteSortOptions';
import { ExplorableItemType } from '../../../spacesPlatform/models/ContentType';
import {
  AccelerationTaskContentType,
  BackgroundJobContentType,
  CustomViewsContentType,
  DataAlertContentType,
  DatabaseContentType,
  DatabaseWithCRASSContentType,
  DatabaseWithCRASSSearchSubspaceType,
  DataConnectionContentType,
  DataConnectionInFlowContentType,
  DataRoleContentType,
  DatasourceContentType,
  ExtractTaskContentType,
  ExtractTaskSingleTargetContentType,
  FlowContentType,
  FlowOutputStepRunContentType,
  GroupContentType,
  GroupUserContentType,
  LensContentType,
  MixedContentType,
  PredictiveModelContentType,
  ProjectContentType,
  PublishedConnectionColumnContentType,
  PublishedConnectionContentType,
  PublishedConnectionTableContentType,
  RemoteRefreshScheduleContentType,
  ResourceGovernanceSettingContentType,
  RunFlowTaskContentType,
  ScheduleContentType,
  ServerUserContentType,
  SitesContentType,
  SiteUserContentType,
  SubscriptionContentType,
  TableContentType,
  TableWithCRASSContentType,
  TableWithCRASSSearchSubspaceType,
  ViewContentType,
  WorkbookAccelerationContentType,
  WorkbookContentType,
} from '../../../spacesPlatform/models/ContentTypes';
import { mixedContentInCrassDefaultSortOrder, mixedContentInCrassSortOptions } from '../../../spacesPlatform/models/MixedContentInCrassSortOptions';
import { mixedContentOptions } from '../../../spacesPlatform/models/MixedContentSortOptions';
import { subscriptionSortOptions } from '../../../subscription/models/sort/SubscriptionSortOptions';
import { groupSortOptions } from '../../../userManagement/users/models/sort/GroupSortOptions';
import { serverUserSortOptions } from '../../../userManagement/users/models/sort/ServerUserSortOptions';
import { siteUserContentDefaultSortOrder, siteUserContentSortOptions } from '../../../userManagement/users/models/sort/SiteUserContentSortOptions';
import { siteUserSortOptions } from '../../../userManagement/users/models/sort/SiteUserSortOptions';
import { defaultUserSort, userSortOptions } from '../../../userManagement/users/models/sort/UserSortOptions';
import { customViewSortOptions } from '../../../views/models/sort/customViews/CustomViewSortOptions';
import { workbookAccelerationSortOptions } from '../../../workbook/models/sort/dataAcceleration/WorkbookAccelerationSortOptions';
import { DataSourceSubspaceWorkbookSortOptions, workbookSortOptions } from '../../../workbook/models/sort/WorkbookSortOptions';
import { WorkbookSubspaceDatasourcesSortOptions, WorkbookSubspaceViewsSortOptions } from '../../../workbook/models/sort/WorkbookSubplaceSortOptions';
import { FeatureFlags } from '../../FeatureFlags';
import { Order } from '../../helpers/Order';
import { IOrderClause } from '../../ServerApi/types/IOrderClause';
import { OrderKeys } from '../OrderKeys';
import { backgroundJobsSortOptions } from './BackgroundJobsSortOptions';
import { SortOptions } from './CommonSortOptions';
import { dataConnectionSortOptions, WorkbookSubspaceDataConnectionSortOptions } from './DataConnectionSortOptions';
import { projectSortOptions } from './ProjectSortOptions';
import { scheduleSortOptions } from './ScheduleSortOptions';
import { viewSortOptions } from './ViewSortOptions';

export type GetDisplayValue = () => string;

export interface OptionDefinition {
  readonly id: string;
  readonly getMenuItemDisplayValue: GetDisplayValue;
  readonly field?: string;
  readonly getButtonDisplayValue?: GetDisplayValue;
  readonly ascending?: boolean;
  readonly toOrderClauseArray?: () => ReadonlyArray<IOrderClause>;
  readonly subSortOptions?: { readonly [s: string]: OptionDefinition };
}

export function getPathToOption(order: IOrderClause, options: OptionsDictionary): OptionDefinition[] {
  return dfs(options, d => d.field === order.field &&
    d.ascending === order.ascending);
}

export function hasPathToOption(order: IOrderClause, options: OptionsDictionary): boolean {
  return getPathToOption(order, options).length > 0;
}

export function getPathToOptionById(id: string, options: OptionsDictionary): OptionDefinition[] {
  return dfs(options, d => d.id === id);
}

export function removeInvalidSortClauses(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo) {
  const validOptions = getOptionsForContentTypes(contentTypes, spaceInfo);
  return _.filter(order, orderClause => hasPathToOption(orderClause, validOptions));
}

export function areValidForTypes(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>) {
  const spaceInfo: SpaceInfo = selectSpaceInfo(getAppStore().getState());
  const options = getOptionsForContentTypes(contentTypes, spaceInfo);
  return _.all(order, orderClause => hasPathToOption(orderClause, options));
}

export function areValid(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo) {
  const options = getOptionsForContentTypes(contentTypes, spaceInfo);
  return _.all(order, orderClause => hasPathToOption(orderClause, options));
}

export function getOptionsForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): OptionsDictionary {
  if (spaceInfo && isWorkbookSpace(spaceInfo)) {
    return sortOptionsByWorkbookSubspace[spaceInfo.subspaceName as WorkbookSubspaceName].options;
  } else if (spaceInfo && isDatasourceSpace(spaceInfo)) {
    return sortOptionsByDatasourceSubspace[spaceInfo.subspaceName as DatasourceSubspaceName].options;
  } else if (spaceInfo && isFlowSpace(spaceInfo)) {
    return sortOptionsByFlowSubspace[spaceInfo.subspaceName as FlowSubspaceName].options;
  } else if (spaceInfo && isDataRoleSpace(spaceInfo)) {
    return {};
  } else if (spaceInfo && isSiteSpace(spaceInfo)) {
    const options = getSortOptionsByContentType(convertSiteContentTypeToContentType(spaceInfo.subspaceName as SiteSpaceContentType));
    // Note: Currently Collections is not setup to return modified by and will break with the sort / filter on.
    return !FeatureFlags.modifiedByUserEnabled ? _.omit(options, 'modifiedBy') : options;
  } else if (spaceInfo && isFavoritesSpace(spaceInfo)) {
    return getFavoritesSortOptions(contentTypes);
  } else if (spaceInfo && isRecentsSpace(spaceInfo)) {
    return FeatureFlags.metricsSpaceEnabled ? recentsSortOptions : recentViewsOptions;
  } else if (spaceInfo && isBackgroundJobsSpace(spaceInfo)) {
    return backgroundJobsSortOptions;
  } else if (spaceInfo && isSiteTasksSpace(spaceInfo)) {
    return getSortOptionsByContentType(siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName) as ExplorableItemType);
  } else if (spaceInfo && isServerTasksSpace(spaceInfo)) {
    return getSortOptionsByContentType(serverTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ServerTasksSubspaceName) as ExplorableItemType);
  } else if (spaceInfo && isUnifiedSearchSpace(spaceInfo)) {
    return mixedContentInCrassSortOptions;
  } else if (spaceInfo && isSearchSpace(spaceInfo)) {
    return getSortOptionsBySearchSubspace(spaceInfo.subspaceName as SearchSubspaceName);
  } else if (spaceInfo && isSiteUserSpace(spaceInfo)) {
    if (spaceInfo.subspaceName === 'settings') {
      return {};
    }
    return getSiteUserContentSortOptions(contentTypes);
  } else if (spaceInfo && isGroupSpace(spaceInfo)) {
    const explorableItemType = groupSubspaceNameToExplorableItemType(spaceInfo.subspaceName as GroupSubspaceName);
    if (explorableItemType === '') {
      return {};
    }
    const options = getSortOptionsByContentType(explorableItemType);
    return options;
  } else if (isScheduleSpace(spaceInfo)) {
    const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
    if (explorableItemType === '') {
      return {};
    } else {
      const options = getSortOptionsByContentType(explorableItemType);
      const inServerArea = NavLocationSelectors.inServerArea(getAppStore().getState());
      if (inServerArea) {
        return { ...options, 'siteName': SortOptions.siteName };
      }
      return options;
    }
  } else if (isSharedWithMeSpace(spaceInfo)) {
    return sharedWithMeSortOptions;
  } else if (isSingleCollectionSpace(spaceInfo)) {
    return getCollectionItemsSortOptions(contentTypes);
  } else if (isCollectionsSpace(spaceInfo)) {
    return getCollectionsSortOptions(spaceInfo);
  } else if (isCatalogSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return getSortOptionsByContentType(catalogContentType);
  } else if (contentTypes.length === 1) {
    return getSortOptionsByContentType(contentTypes[0], (isPersonalSpace(spaceInfo) || isProjectSpace(spaceInfo)));
  } else if (isCatalogSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return getSortOptionsByContentType(catalogContentType);
  } else if(isResourceGovernanceSpace(spaceInfo)) {
    return resourceGovernanceSettingSortOptions;
  } else if(isConnectedApplicationSpace(spaceInfo)) {
    return connectedApplicationSortOptions;
  } else {
    return mixedContentOptions;
  }
}

function getFavoritesSortOptions(contentTypes: ReadonlyArray<ExplorableItemType>) {
  if (contentTypes.length === 1) {
    const favoritesSorts =  { 'position': SortOptions.position, 'dateAdded': SortOptions.dateAdded };
    return { ...getSortOptionsByContentType(contentTypes[0]), ...favoritesSorts };
  } else {
    return favoritesMixedContentOptions;
  }
}

function getCollectionItemsSortOptions(contentTypes: ReadonlyArray<ExplorableItemType>) {

  const collectionItemsSortOptions = FeatureFlags.reorderCollectionsEnabled?
      collectionItemsSpecificSortOptions :
      collectionItemsSpecificSortOptionsWithoutCustomSort;

  if (contentTypes.length === 1) {
    let customSortOptions = getSortOptionsByContentType(contentTypes[0], true);
    // TFS 1235644 to remove this delete once Relevance is removed from sorting.
    // Relevance doesn't exist as a field under CRASS's /browse API and was erroring out
    // when the user sorted or filtered in Collection Items.
    customSortOptions = _.omit(customSortOptions, 'relevance');

    // Note: Currently Collections is not setup to return modified by and will break with the sort / filter on.
    customSortOptions = !FeatureFlags.modifiedByUserEnabled ? _.omit(customSortOptions, 'modifiedBy') : customSortOptions;

    // sorting on permissioned subcontent counts isn't supported in collection items
    if (contentTypes[0] === 'project') {
      customSortOptions = _.omit(customSortOptions, 'projects', 'workbooks', 'views', 'datasources');
    }

    if (contentTypes[0] === 'datasource') {
      customSortOptions = _.omit(customSortOptions, 'refresh');
    }

      return {
      ...customSortOptions,
      ...collectionItemsSortOptions
    };
  }
  else {
    return {
      ...collectionItemsMixedContentOptionsBase,
      ...collectionItemsSortOptions
    };
  }
}

function getSiteUserContentSortOptions(contentTypes: ReadonlyArray<ExplorableItemType>) {
  if (contentTypes.length === 1) {
    return _.omit(getSortOptionsByContentType(contentTypes[0]), 'owner');
  } else {
    return siteUserContentSortOptions;
  }
}

function getSortOptionsByContentType(contentType: ExplorableItemType, shouldHideContainerName?: boolean): OptionsDictionary {
  let sortOptions = sortOptionsByContentType[contentType].options;

  if(contentType === 'view' && !FeatureFlags.LocationNameSortEnabled) {
    sortOptions = getSortOptionsWithContainerName(sortOptions, shouldHideContainerName);
  }

  if(shouldHideContainerName) {
    sortOptions = _.omit(sortOptions, 'containerName');
  }

  if (!FeatureFlags.collectionsGAEnabled && contentType === 'collection') {
    sortOptions = collectionsSortOptions;
  }

  return sortOptions;
}

export function getDisplaySortDropdownForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): boolean {
  if (spaceInfo && isWorkbookSpace(spaceInfo)) {
    return sortOptionsByWorkbookSubspace[spaceInfo.subspaceName as WorkbookSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isDatasourceSpace(spaceInfo)) {
    return sortOptionsByDatasourceSubspace[spaceInfo.subspaceName as DatasourceSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isFlowSpace(spaceInfo)) {
    return sortOptionsByFlowSubspace[spaceInfo.subspaceName as FlowSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isPublishedConnectionSpace(spaceInfo)) {
    return sortOptionsByPublishedConnectionSubspace[spaceInfo.subspaceName as PublishedConnectionSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isPublishedConnectionTableSpace(spaceInfo)) {
    return sortOptionsByPublishedConnectionTableSubspace[spaceInfo.subspaceName as PublishedConnectionTableSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isDataRoleSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isVizSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isRecommendationsSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isSiteSpace(spaceInfo)) {
    return sortOptionsByContentType[convertSiteContentTypeToContentType(spaceInfo.subspaceName as SiteSpaceContentType)].displaySortDropdown;
  } else if (spaceInfo && isSearchSpace(spaceInfo)) {
    return sortOptionsBySearchSubspace[spaceInfo.subspaceName as SearchSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isSharedWithMeSpace(spaceInfo)) {
    // Note: change it to true to allow SortBy option
    return false;
  } else if (contentTypes.length === 1 && sortOptionsByContentType[contentTypes[0]]) {
    return sortOptionsByContentType[contentTypes[0]].displaySortDropdown;
  } else if (spaceInfo && (isSiteTasksSpace(spaceInfo) || isServerTasksSpace(spaceInfo))) {
    return false;
  } else if (spaceInfo && isGroupSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isSingleCollectionSpace(spaceInfo)) {
    return FeatureFlags.collectionsGAEnabled;
  } else if (spaceInfo && isCatalogSpace(spaceInfo)) {
    return true;
  } else { // includes isFavoritesSpace and RecentsSpace
    return true;
  }
}

function getSortOptionsBySearchSubspace(searchSubspaceName: SearchSubspaceName): OptionsDictionary {
  let sortOptions = sortOptionsBySearchSubspace[searchSubspaceName].options;
  if (searchSubspaceName === 'views' && !FeatureFlags.LocationNameSortEnabled) {
    sortOptions = getSortOptionsWithContainerName(sortOptions);
  }
  return sortOptions;
}

function getSortOptionsWithContainerName(options: OptionsDictionary, shouldHideLocationName?: boolean): OptionsDictionary {
  let optionsWithoutLocationName = _.omit(options, ['locationName']);
  let containerNameOption = shouldHideLocationName ? {} : { 'containerName': SortOptions.containerName };
  return { ...optionsWithoutLocationName, ...containerNameOption };
}

export function getDefaultOrderForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>): IOrderClause[] {
  return getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, null);
}

export function getDefaultOrderForSpaceInfo(spaceInfo: SpaceInfo): IOrderClause[] {
  return getDefaultOrderForContentTypesOrSpaceInfo([], spaceInfo);
}

export function getDefaultOrderForFlowSubSpace(spaceInfo: SpaceInfo): IOrderClause[] {
  return sortOptionsByFlowSubspace[spaceInfo.subspaceName as FlowSubspaceName].defaultOrder;
}

export function getDefaultOrderForPublishedConnectionSubSpace(spaceInfo: SpaceInfo): IOrderClause[] {
  return sortOptionsByPublishedConnectionSubspace[spaceInfo.subspaceName as PublishedConnectionSubspaceName].defaultOrder;
}

export function getDefaultOrderForPublishedConnectionTableSubSpace(spaceInfo: SpaceInfo): IOrderClause[] {
  return sortOptionsByPublishedConnectionTableSubspace[spaceInfo.subspaceName as PublishedConnectionTableSubspaceName].defaultOrder;
}

export const EmptySortOrder: IOrderClause[] = [];

export function getDefaultOrderForContentTypesOrSpaceInfo(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): IOrderClause[] {
  if (spaceInfo && isWorkbookSpace(spaceInfo)) {
    return sortOptionsByWorkbookSubspace[spaceInfo.subspaceName as WorkbookSubspaceName].defaultOrder;
  } else if (spaceInfo && isDatasourceSpace(spaceInfo)) {
    return sortOptionsByDatasourceSubspace[spaceInfo.subspaceName as DatasourceSubspaceName].defaultOrder;
  } else if (spaceInfo && isFlowSpace(spaceInfo)) {
    return getDefaultOrderForFlowSubSpace(spaceInfo);
  } else if (spaceInfo && isPublishedConnectionSpace(spaceInfo)) {
    return getDefaultOrderForPublishedConnectionSubSpace(spaceInfo);
  } else if (spaceInfo && isPublishedConnectionTableSpace(spaceInfo)) {
    return getDefaultOrderForPublishedConnectionTableSubSpace(spaceInfo);
  } else if (spaceInfo && isSiteSpace(spaceInfo)) {
    return sortOptionsByContentType[convertSiteContentTypeToContentType(spaceInfo.subspaceName as SiteSpaceContentType)].defaultOrder;
  } else if (spaceInfo && isFavoritesSpace(spaceInfo)) {
    return favoritesSpaceDefaultSortOrder;
  } else if (spaceInfo && isRecentsSpace(spaceInfo)) {
    return recentsSpaceDefaultSortOrder;
  } else if (spaceInfo && isSharedWithMeSpace(spaceInfo)) {
    return sharedWithMeDefaultSortOrder;
  } else if (spaceInfo && isPersonalSpace(spaceInfo)) {
    return WorkbookContentType.defaultOrder;
  } else if (spaceInfo && isSearchSpace(spaceInfo)) {
    return sortOptionsBySearchSubspace[spaceInfo.subspaceName as SearchSubspaceName].defaultOrder;
  } else if (spaceInfo && isUnifiedSearchSpace(spaceInfo)) {
    return mixedContentInCrassDefaultSortOrder;
  } else if (spaceInfo && isBackgroundJobsSpace(spaceInfo)) {
    return BackgroundJobContentType.defaultOrder;
  } else if (spaceInfo && isGroupSpace(spaceInfo)) {
    if (spaceInfo.subspaceName === 'details') {
      return EmptySortOrder;
    } else {
      return GroupUserContentType.defaultOrder;
    }
  } else if (spaceInfo && isSiteUserSpace(spaceInfo)) {
    if (spaceInfo.subspaceName === 'settings') {
      return EmptySortOrder;
    }

    if (contentTypes.length === 1) {
      return sortOptionsByContentType[contentTypes[0]].defaultOrder;
    }

    return siteUserContentDefaultSortOrder;
  } else if (isScheduleSpace(spaceInfo)) {
    const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
    if (explorableItemType === '') {
      return EmptySortOrder;
    } else {
      return sortOptionsByContentType[explorableItemType].defaultOrder;
    }
  } else if (isSiteTasksSpace(spaceInfo)) {
    const explorableItemType = siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName);
    if (explorableItemType === '') {
      return EmptySortOrder;
    } else {
      return sortOptionsByContentType[explorableItemType].defaultOrder;
    }
  } else if (isServerTasksSpace(spaceInfo)) {
    const explorableItemType = serverTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ServerTasksSubspaceName);
    if (explorableItemType === '') {
      return EmptySortOrder;
    } else {
      return sortOptionsByContentType[explorableItemType].defaultOrder;
    }
  } else if (isSingleCollectionSpace(spaceInfo)) {
    return FeatureFlags.reorderCollectionsEnabled? collectionItemPositionSortOrder: collectionItemDateSortOrder;
  } else if (contentTypes.length === 1) {
    return sortOptionsByContentType[contentTypes[0]].defaultOrder;
  } else if (contentTypes.length > 1 || isProjectSpace(spaceInfo)) {
    return MixedContentType.defaultOrder;
  } else if (isCatalogSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return sortOptionsByContentType[catalogContentType].defaultOrder;
  } else if (isResourceGovernanceSpace(spaceInfo)) {
    return ResourceGovernanceSettingContentType.defaultOrder;
  } else if(isConnectedApplicationSpace(spaceInfo)) {
    return ConnectedApplicationContentType.defaultOrder;
  } else if (isAllSiteUsersSpace(spaceInfo)) {
    return SiteUserContentType.defaultOrder;
  } else {
    return EmptySortOrder;
  }
}

export type OptionsDictionary = { readonly [s: string]: OptionDefinition };

export interface SortOptionsForType {
  defaultOrder: IOrderClause[];
  options: OptionsDictionary;
  displaySortDropdown: boolean;
}

// exported for testing
export const sortOptionsByContentType: { [s in ExplorableItemType]: SortOptionsForType } = {
  'accelerationTask': { defaultOrder: AccelerationTaskContentType.defaultOrder, options: dataAccelerationSortOptions, displaySortDropdown: false },
  'backgroundJob': { defaultOrder: BackgroundJobContentType.defaultOrder, options: backgroundJobsSortOptions, displaySortDropdown: true },
  'collection': { defaultOrder: defaultCollectionSort, options: collectionsSortOptionsWithCollectionsGA, displaySortDropdown: true },
  'connectedApplication': { defaultOrder: ConnectedApplicationContentType.defaultOrder, options: connectedApplicationSortOptions, displaySortDropdown: false },
  'connectedWorkbook': { defaultOrder: WorkbookContentType.defaultOrder, options: workbookSortOptions, displaySortDropdown: true },
  'connection': { defaultOrder: DataConnectionContentType.defaultOrder, options: dataConnectionSortOptions, displaySortDropdown: true },
  'customView': { defaultOrder: CustomViewsContentType.defaultOrder, options: customViewSortOptions, displaySortDropdown: false },
  'dataalert': { defaultOrder: DataAlertContentType.defaultOrder, options: dataAlertSortOptions, displaySortDropdown: false },
  'database': { defaultOrder: DatabaseContentType.defaultOrder, options: databaseDropdownSortOptions, displaySortDropdown: false },
  'databaseWithCrass': { defaultOrder: DatabaseWithCRASSContentType.defaultOrder, options: databaseWithCRASSSortOptions, displaySortDropdown: true },
  'dataconnection': { defaultOrder: DataConnectionContentType.defaultOrder, options: dataConnectionSortOptions, displaySortDropdown: true },
  'dataRole': { defaultOrder: DataRoleContentType.defaultOrder, options: dataRoleSortOptions, displaySortDropdown: true },
  'datasource': { defaultOrder: DatasourceContentType.defaultOrder, options: datasourceSortOptions, displaySortDropdown: true },
  'extractTask': { defaultOrder: ExtractTaskContentType.defaultOrder, options: extractRefreshSortOptions, displaySortDropdown: false },
  'flow': { defaultOrder: FlowContentType.defaultOrder, options: flowSortOptions, displaySortDropdown: true },
  'flowOutputStepRun': { defaultOrder: FlowOutputStepRunContentType.defaultOrder, options: flowOutputStepRunSortOptions, displaySortDropdown: false },
  'group': { defaultOrder: GroupContentType.defaultOrder, options: groupSortOptions, displaySortDropdown: false },
  'lens': { defaultOrder: LensContentType.defaultOrder, options: lensSortOptions, displaySortDropdown: true },
  'metric': { defaultOrder: MetricContentType.defaultOrder, options: metricsSortOptions, displaySortDropdown: true },
  'predictiveModel': { defaultOrder: PredictiveModelContentType.defaultOrder, options: predictiveModelSortOptions, displaySortDropdown: true },
  'project': { defaultOrder: ProjectContentType.defaultOrder, options: projectSortOptions, displaySortDropdown: true },
  'virtualConnection': { defaultOrder: PublishedConnectionContentType.defaultOrder, options: publishedConnectionSortOptions, displaySortDropdown: true },
  'virtualConnectionTable': { defaultOrder: PublishedConnectionTableContentType.defaultOrder, options: publishedConnectionTableSortOptions, displaySortDropdown: false },
  'virtualConnectionColumn': { defaultOrder: PublishedConnectionColumnContentType.defaultOrder, options: publishedConnectionColumnSortOptions, displaySortDropdown: false },
  'recommendedView': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'remoteRefreshSchedule': { defaultOrder: RemoteRefreshScheduleContentType.defaultOrder, options: remoteRefreshSortOptions, displaySortDropdown: false },
  'runFlowTask': { defaultOrder: RunFlowTaskContentType.defaultOrder, options: runFlowTasksSortOptions, displaySortDropdown: false },
  'resourceGovernanceSetting': { defaultOrder: ResourceGovernanceSettingContentType.defaultOrder, options: resourceGovernanceSettingSortOptions, displaySortDropdown: false },
  'schedule': { defaultOrder: ScheduleContentType.defaultOrder, options: scheduleSortOptions, displaySortDropdown: false },
  'searchuser': { defaultOrder: defaultUserSort, options: userSortOptions, displaySortDropdown: false },
  'serveruser': { defaultOrder: ServerUserContentType.defaultOrder, options: serverUserSortOptions, displaySortDropdown: false },
  'userIdentityConflict': { defaultOrder: [], options: userIdentityConflictSortOptions, displaySortDropdown: false },
  'site': { defaultOrder: SitesContentType.defaultOrder, options: siteSortOptions, displaySortDropdown: false },
  'siteuser': { defaultOrder: SiteUserContentType.defaultOrder, options: siteUserSortOptions, displaySortDropdown: false },
  'subscription': { defaultOrder: SubscriptionContentType.defaultOrder, options: subscriptionSortOptions, displaySortDropdown: false },
  'table': { defaultOrder: TableContentType.defaultOrder, options: tableDropdownSortOptions, displaySortDropdown: false },
  'tableWithCrass': { defaultOrder: TableWithCRASSContentType.defaultOrder, options: tableWithCRASSSortOptions, displaySortDropdown: true },
  'view': { defaultOrder: ViewContentType.defaultOrder, options: viewSortOptions, displaySortDropdown: true },
  'workbook': { defaultOrder: WorkbookContentType.defaultOrder, options: workbookSortOptions, displaySortDropdown: true },
  'workbookAcceleration': { defaultOrder: WorkbookAccelerationContentType.defaultOrder, options: workbookAccelerationSortOptions, displaySortDropdown: false },
};

export const sortOptionsByWorkbookSubspace: { [s in WorkbookSubspaceName]: SortOptionsForType } = {
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'datasources': WorkbookSubspaceDatasourcesSortOptions,
  'customViews': { defaultOrder: CustomViewsContentType.defaultOrder, options: customViewSortOptions, displaySortDropdown: false },
  'dataconnections': { defaultOrder: DataConnectionContentType.defaultOrder, options: WorkbookSubspaceDataConnectionSortOptions, displaySortDropdown: true },
  'views': WorkbookSubspaceViewsSortOptions,
  'subscriptions': { defaultOrder: SubscriptionContentType.defaultOrder, options: subscriptionSortOptions, displaySortDropdown: false },
  'extractTasks': { defaultOrder: ExtractTaskSingleTargetContentType.defaultOrder, options: extractRefreshSortOptions, displaySortDropdown: false },
  'connectedMetrics': { defaultOrder: MetricContentType.defaultOrder, options: metricsSortOptions, displaySortDropdown: true },
  'workbookAccelerations': { defaultOrder: WorkbookAccelerationContentType.defaultOrder, options: workbookAccelerationSortOptions, displaySortDropdown: false },
};

const sortOptionsByDatasourceSubspace: { [s in DatasourceSubspaceName]: SortOptionsForType } = {
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'connections': { defaultOrder: DatasourceSubspaceConnectionsSortOptions.defaultOrder, options: DatasourceSubspaceConnectionsSortOptions.options, displaySortDropdown: true },
  'extractRefreshes': { defaultOrder: ExtractTaskSingleTargetContentType.defaultOrder, options: extractRefreshSortOptions, displaySortDropdown: false },
  'nlp': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'remoteRefreshSchedules': { defaultOrder: RemoteRefreshScheduleContentType.defaultOrder, options: remoteRefreshSortOptions, displaySortDropdown: false },
  'connectedWorkbooks': { defaultOrder: WorkbookContentType.defaultOrder, options: DataSourceSubspaceWorkbookSortOptions, displaySortDropdown: true },
  'askDataUsageAnalytics': { defaultOrder: [], options: {}, displaySortDropdown: false },
};

const sortOptionsByFlowSubspace: { [s in FlowSubspaceName]: SortOptionsForType } = {
  'overview': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'connections': { defaultOrder: DataConnectionInFlowContentType.defaultOrder, options: FlowConnectionsSortOptions, displaySortDropdown: false },
  'scheduledTasks': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'runHistory': { defaultOrder: FlowOutputStepRunContentType.defaultOrder, options: flowOutputStepRunSortOptions, displaySortDropdown: true },
  'subscriptions': { defaultOrder: SubscriptionContentType.defaultOrder, options: subscriptionSortOptions, displaySortDropdown: false },
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
};

// exported so that it can be used directly in the omni box (search panel dropdown)
export const sortOptionsBySearchSubspace: { [s in SearchSubspaceName]: SortOptionsForType } = {
  'views': { defaultOrder: [Order.desc(OrderKeys.hitsLastThreeMonthsTotal), Order.desc(OrderKeys.hitsTotal)], options: viewSortOptions, displaySortDropdown: true },
  'metrics': { defaultOrder: [Order.desc(OrderKeys.hitsLastThreeMonthsTotal), Order.desc(OrderKeys.hitsTotal)], options: metricsSortOptions, displaySortDropdown: true },
  'workbooks': { defaultOrder: [Order.desc(OrderKeys.hitsLastThreeMonthsTotal), Order.desc(OrderKeys.hitsTotal)], options: workbookSortOptions, displaySortDropdown: true },
  'predictiveModels': { defaultOrder: [Order.asc(OrderKeys.name)], options: predictiveModelSortOptions, displaySortDropdown: true },
  'projects': { defaultOrder: [Order.asc(OrderKeys.name)], options: projectSortOptions, displaySortDropdown: true },
  'datasources': { defaultOrder: [Order.desc(OrderKeys.isCertified), Order.desc(OrderKeys.relevance)], options: datasourceSortOptionsForSearch, displaySortDropdown: true },
  'flows': { defaultOrder: [Order.asc(OrderKeys.name)], options: flowSortOptions, displaySortDropdown: true },
  'dataRoles': { defaultOrder: DataRoleContentType.defaultOrder, options: dataRoleSortOptions, displaySortDropdown: true },
  'lenses': { defaultOrder: LensContentType.defaultOrder, options: lensSortOptions, displaySortDropdown: true },
  'databases': { defaultOrder: DatabaseContentType.defaultOrder, options: databaseDropdownSortOptions, displaySortDropdown: false },
  'tables': { defaultOrder: TableContentType.defaultOrder, options: tableDropdownSortOptions, displaySortDropdown: false },
  'databasesWithCrass': { defaultOrder: DatabaseWithCRASSSearchSubspaceType.defaultOrder, options: databaseWithCRASSSortOptions, displaySortDropdown: true },
  'tablesWithCrass': { defaultOrder: TableWithCRASSSearchSubspaceType.defaultOrder, options: tableWithCRASSSortOptions, displaySortDropdown: true },
  'users': { defaultOrder: defaultUserSort, options: userSortOptions, displaySortDropdown: false },
  'virtualConnections': { defaultOrder: PublishedConnectionContentType.defaultOrder, options: publishedConnectionSortOptions, displaySortDropdown: true },
};

export const sortOptionsByPublishedConnectionSubspace: { [s in PublishedConnectionSubspaceName]: SortOptionsForType } = {
  'tables': { defaultOrder: PublishedConnectionTableContentType.defaultOrder, options: publishedConnectionTableSortOptions, displaySortDropdown: false },
  'scheduledTasks': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
};

export const sortOptionsByPublishedConnectionTableSubspace: { [s in PublishedConnectionTableSubspaceName]: SortOptionsForType } = {
  'overview': { defaultOrder: PublishedConnectionColumnContentType.defaultOrder, options: publishedConnectionColumnSortOptions, displaySortDropdown: false },
  'scheduledTasks': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
};

function dfs(dictionary: { [s: string]: OptionDefinition }, predicate: (d: OptionDefinition) => boolean): OptionDefinition[] {
  const stack: OptionDefinition[] = _.values(dictionary);
  const parents: { [s: string]: OptionDefinition } = {};
  const seen: { [s: string]: boolean } = {};
  let found: OptionDefinition;

  while (stack.length > 0) {
    const v = stack.pop();
    if (predicate(v)) {
      found = v;
      break;
    }
    if (v.subSortOptions) {
      for (const child of _.values(v.subSortOptions)) {
        if (!seen[child.id]) {
          seen[child.id] = true;
          parents[child.id] = v;
          stack.push(child);
        }
      }
    }
  }

  if (found) {
    const path = [found];
    let parent = parents[found.id];
    while (parent) {
      path.unshift(parent);
      parent = parents[parent.id];
    }
    return path;
  } else {
    return [];
  }
}
